import { useMemo, useState, type ReactNode } from 'react'
import {
  Badge,
  Button,
  DataTable,
  Label,
  MultiSelect,
  SearchInput,
  cn,
  type DataTableColumn,
  type DataTableSortState,
} from '@atrvd/saas-ui'
import { X } from 'lucide-react'
import { formatCurrency } from '@dcp/shared/utils/formatters'
import {
  usePendingSageIncludeIds,
  useSetSageIncludeInCashPosition,
} from '@/hooks/useSageIntegration'
import { useTablePagination } from '@/hooks/useTablePagination'
import { queryErrorDisplay, type TableQueryStateProps } from '@/components/common/queryErrorDisplay'
import type { SageBankAccountRow } from '@/lib/db-tenant'
import type { CheckAggregate } from '@/lib/sage-utils'
import type { Facility } from '@/types/banking'
import {
  SAGE_BANK_ACCOUNT_COLUMNS as COLUMNS,
  facilityFilterOptions,
  filterSageBankAccountRows,
  hasActiveFilters,
  sortSageBankAccountRows,
  toSageBankAccountTableRows,
  withoutInactiveFacilityRows,
  type SageBankAccountTableRow,
} from './sageBankAccountsTable.utils'
import { SageIncludeToggle } from './SageIncludeToggle'

const NO_ROWS: SageBankAccountTableRow[] = []
const DEFAULT_SORT: DataTableSortState[] = [{ columnKey: COLUMNS.name, direction: 'asc' }]
const INCLUDE_HINT =
  'Accounts enabled here appear in the Intacct Balances list and their Intacct balances are included in the Cash Position Summary.'

/**
 * `whitespace-nowrap`: a wrapped header label drifts off its cells inside the
 * sort button; the table scrolls horizontally instead.
 */
const HEADER_CLASS = 'whitespace-nowrap'

/** Highlights a MultiSelect whose filter is set (same cue as the manual-bank filters). */
const ACTIVE_FILTER = '[&_[role=combobox]]:border-blue-200'

interface SageBankAccountsTableProps {
  /** Every Sage bank account, inactive and missing-in-Sage ones included. */
  accounts: SageBankAccountRow[]
  /** State of the accounts query, so "nothing synced yet" is never claimed while loading or after a failure. */
  accountsQuery: TableQueryStateProps
  /**
   * Every facility, inactive ones included: the table resolves each account's
   * winning facility itself and hides the accounts of inactive ones.
   */
  facilities: Facility[]
  /**
   * State of the facilities query. Until a list is available every account
   * would read as unmapped, showing inactive facilities' accounts with live toggles.
   */
  facilitiesQuery: TableQueryStateProps
  checksByEntity: ReadonlyMap<string, CheckAggregate>
  /** Before the first checks sync a zero count is unknown, so it renders as a dash. */
  checksSyncRanAtLeastOnce: boolean
  /** Whether the user may change Include in Cash Position. */
  canEdit: boolean
}

/**
 * The Sage bank-account catalog on Settings → Sage Intacct: search, facility
 * filter, sorting on every column, check counts and the per-account Include in
 * Cash Position toggle.
 *
 * Everything is client-side — the catalog is already loaded in full — and the
 * whole filtered set is sorted before it is paged, so a sort orders every
 * account rather than the visible page. Accounts of inactive facilities are
 * hidden before anything else (ADR 0016), so the Facility filter only offers
 * active facilities.
 */
export function SageBankAccountsTable({
  accounts,
  accountsQuery,
  facilities,
  facilitiesQuery,
  checksByEntity,
  checksSyncRanAtLeastOnce,
  canEdit,
}: SageBankAccountsTableProps) {
  const [search, setSearch] = useState('')
  const [facilityFilter, setFacilityFilter] = useState<string[]>([])
  const [sort, setSort] = useState<DataTableSortState[]>(DEFAULT_SORT)
  const { mutate: mutateInclude } = useSetSageIncludeInCashPosition()
  // Every write in flight, not just the latest: toggling B must keep A locked until A lands.
  const pendingIds = usePendingSageIncludeIds()

  const rows = useMemo(
    () =>
      withoutInactiveFacilityRows(toSageBankAccountTableRows(accounts, facilities, checksByEntity)),
    [accounts, facilities, checksByEntity],
  )
  const facilityOptions = useMemo(() => facilityFilterOptions(rows), [rows])
  const filters = { search, facilityIds: facilityFilter }
  const filtered = hasActiveFilters(filters)
  const sortedRows = sortSageBankAccountRows(filterSageBankAccountRows(rows, filters), sort)
  const loading = accountsQuery.loading || facilitiesQuery.loading

  // Every change of the result set (search, facility filter, sort) lands on its
  // first page; a toggle or sync refetch shrinking the list is followed back.
  const pagination = useTablePagination({
    // Unknown while loading: rows of inactive facilities are not filtered out yet.
    totalItems: loading ? undefined : sortedRows.length,
    resetKey: JSON.stringify([search, facilityFilter, sort]),
  })
  const pagedRows = pagination.paginate(sortedRows)

  const columns = useMemo<DataTableColumn<SageBankAccountTableRow>[]>(() => {
    return [
      {
        key: COLUMNS.name,
        header: 'Bank account',
        headerClassName: HEADER_CLASS,
        sortable: true,
        render: ({ account }) => {
          const isInactive = account.status === 'inactive' || account.missing_in_sage
          return (
            <div className="flex flex-col">
              <span className={isInactive ? 'text-muted-foreground' : 'font-medium'}>
                {account.name}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {account.financial_entity_id}
              </span>
              {account.missing_in_sage && (
                <Badge variant="warning" className="mt-1 w-fit">
                  missing in Sage
                </Badge>
              )}
              {!account.missing_in_sage && account.status === 'inactive' && (
                <Badge variant="secondary" className="mt-1 w-fit">
                  inactive
                </Badge>
              )}
            </div>
          )
        },
      },
      {
        key: COLUMNS.accountNumber,
        header: 'Account #',
        headerClassName: HEADER_CLASS,
        sortable: true,
        cellClassName: 'font-mono text-sm',
        render: ({ account }) => account.bank_account_no ?? '—',
      },
      {
        key: COLUMNS.facility,
        header: 'Facility',
        headerClassName: HEADER_CLASS,
        sortable: true,
        render: (row) => row.facilityName ?? <span className="text-muted-foreground">—</span>,
      },
      {
        key: COLUMNS.checks,
        header: 'All checks',
        headerClassName: HEADER_CLASS,
        cellClassName: 'tabular-nums',
        sortable: true,
        render: ({ checks }) =>
          checks.total === 0 ? (
            <span className="text-muted-foreground">{checksSyncRanAtLeastOnce ? '0' : '—'}</span>
          ) : (
            <span className="font-medium">{checks.total}</span>
          ),
      },
      {
        key: COLUMNS.outstanding,
        header: 'Outstanding',
        headerClassName: HEADER_CLASS,
        cellClassName: 'tabular-nums',
        sortable: true,
        render: ({ checks }) =>
          checks.outstanding > 0 ? (
            <span className="font-medium">
              {checks.outstanding}{' '}
              <span className="text-xs text-muted-foreground">
                ({formatCurrency(checks.outstandingAmount)})
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">
              {checks.total === 0 && !checksSyncRanAtLeastOnce ? '—' : '0'}
            </span>
          ),
      },
      {
        key: COLUMNS.include,
        header: 'Include in Cash Position',
        headerClassName: HEADER_CLASS,
        headerHint: INCLUDE_HINT,
        sortable: true,
        render: ({ account }) => (
          <SageIncludeToggle
            account={account}
            disabled={!canEdit || pendingIds.includes(account.financial_entity_id)}
            onChange={(checked) =>
              mutateInclude({ ids: [account.financial_entity_id], include: checked })
            }
          />
        ),
      },
    ]
  }, [pendingIds, mutateInclude, canEdit, checksSyncRanAtLeastOnce])

  // The accounts' failure goes through the shared policy on their own count:
  // with accounts loaded it is a banner (a failed refetch keeps the data),
  // without them the error with Retry takes the table's place.
  const accountsError = queryErrorDisplay(accountsQuery, accounts.length)
  // Facilities error shows here only when no facility list is available; once loaded, the coverage table above reports a failed refetch.
  const facilitiesTableError =
    facilitiesQuery.error && facilities.length === 0
      ? queryErrorDisplay(
          {
            ...facilitiesQuery,
            error: new Error(
              `Facilities could not be loaded, so Sage bank accounts cannot be shown: ${facilitiesQuery.error.message}`,
            ),
          },
          0,
        ).tableError
      : undefined

  // While either list is pending the table shows its skeleton; only the settled
  // states below return early.
  // An empty catalog after a failure is unknown, not empty.
  if (accountsError.tableError) return <SageBankAccountsLoadError columns={columns} error={accountsError.tableError} />
  if (accounts.length === 0 && !accountsQuery.loading) {
    return (
      <p className="text-sm text-muted-foreground">
        No Sage bank accounts synced yet — run Sync Bank Accounts first.
      </p>
    )
  }
  // Without a facility list the inactive-facility scope cannot be applied. A
  // list already loaded survives a failed refetch, the same as the accounts.
  if (facilitiesTableError) return <SageBankAccountsLoadError columns={columns} error={facilitiesTableError} />
  // Only the inactive-facility scope can empty a loaded catalog before any
  // filter applies, so name that cause rather than "nothing synced".
  if (rows.length === 0 && !loading) {
    return (
      <div className="space-y-3">
        {accountsError.banner}
        <p className="text-sm text-muted-foreground">
          All Sage bank accounts belong to inactive facilities. Reactivate a facility in Settings → Facilities to see its accounts.
        </p>
      </div>
    )
  }

  const clearFilters = () => {
    setSearch('')
    setFacilityFilter([])
  }

  return (
    <div className="space-y-3">
      {!canEdit && (
        <p className="text-sm text-muted-foreground">
          Only administrators can change which accounts are included in Cash Position.
        </p>
      )}
      {accountsError.banner}
      {/* Labels are screen-reader only: the placeholders name each control on screen. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[220px] max-w-[340px] flex-1">
          <Label htmlFor="sage-bank-account-search" className="sr-only">
            Search
          </Label>
          <SearchInput
            id="sage-bank-account-search"
            placeholder="Search account name or number"
            value={search}
            onChange={setSearch}
            clearable
          />
        </div>
        <Label htmlFor="sage-bank-account-facility-filter" className="sr-only">
          Facility
        </Label>
        <MultiSelect
          options={facilityOptions}
          value={facilityFilter}
          onChange={setFacilityFilter}
          placeholder="All facilities"
          className={cn('w-[200px]', facilityFilter.length > 0 && ACTIVE_FILTER)}
          searchable
          searchPlaceholder="Search facilities..."
          id="sage-bank-account-facility-filter"
        />
        {filtered && (
          <Button type="button" variant="ghost" onClick={clearFilters}>
            <X aria-hidden="true" className="h-4 w-4" />
            Clear filters
          </Button>
        )}
      </div>

      <DataTable<SageBankAccountTableRow>
        columns={columns}
        data={pagedRows}
        rowKey={(row) => row.account.financial_entity_id}
        sorting={{
          // 'server' = "already sorted": the rows arrive sorted across the whole set, then paged.
          mode: 'server',
          sortState: sort,
          // The third click of a header clears the sort: back to the default order.
          onSortChange: (next) => setSort(next.length > 0 ? next : DEFAULT_SORT),
        }}
        pagination={pagination.dataTablePagination}
        loading={loading}
        refreshing={accountsQuery.refreshing || facilitiesQuery.refreshing}
        emptyState={
          <p className="text-sm text-muted-foreground">No bank accounts match these filters.</p>
        }
        itemName="accounts"
      />
    </div>
  )
}

/**
 * A list the table needs failed before it ever loaded: the error with Retry in
 * the table's place, without the search and filters that have nothing to act on.
 */
function SageBankAccountsLoadError({
  columns,
  error,
}: {
  columns: DataTableColumn<SageBankAccountTableRow>[]
  error: ReactNode
}) {
  return (
    <DataTable<SageBankAccountTableRow>
      columns={columns}
      data={NO_ROWS}
      rowKey={(row) => row.account.financial_entity_id}
      error={error}
      itemName="accounts"
    />
  )
}
